/**
 * TaskGroupedView — Visualização "Lista Agrupada Por:" da página de Tarefas
 *
 * Renderiza a mesma fonte de dados (`useTasksPaginated`) em seções
 * colapsáveis agrupadas por um campo escolhido pelo usuário: Fase,
 * Status, Prioridade ou Responsável.
 *
 * Princípios de reuso:
 * - Cada grupo expandido monta `<TaskListTable />` — exatamente a mesma
 *   tabela, mesmas colunas e mesmos componentes da listagem padrão.
 * - Toda a interação (sort, expand subtasks, status, bulk edit, modais)
 *   vem do `TaskListTableContext` provido por `TaskList`.
 * - O wrapper `overflow-x-auto` em volta da tabela garante que as
 *   colunas mantenham as larguras nativas e permite scroll horizontal
 *   em telas estreitas — paridade total com a listagem normal.
 *
 * @version 2.0.0
 * RELEASE: 2026-06-24 v2.0.0 - Reusa <TaskListTable /> por grupo
 *          (mesmas colunas/componentes/scroll horizontal). Remove o
 *          layout div+flex próprio (COL/TaskGroupHeader/TaskGroupRow).
 * RELEASE: 2026-06-24 v1.2.0 - Cabeçalho de colunas por grupo expandido.
 * RELEASE: 2026-06-24 v1.1.0 - "Pendente" no agrupamento por Status;
 *          agrupamento por Fase agora aninha Categoria → Fase.
 * RELEASE: 2026-06-23 v1.0.0 - Criação inicial (terceira view).
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Loader2, User } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  useTasksPaginated,
  type TaskListFilters,
  type TaskListItem,
} from '@/hooks/useTasksPaginated';
import { useTenantMembers } from '@/hooks/useActivityComments';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { PRIORITY_LABELS } from '@/components/tasks/PriorityFlag';
import { cn } from '@/lib/utils';
import {
  PRIORITY_BAR_COLORS,
  STATUS_LABELS,
} from '@/components/tasks/list/taskListShared';
import { TaskListTable } from '@/components/tasks/list/TaskListTable';
import { useTaskGroupExpandedState } from '@/hooks/useTasksListViewPreference';

/** Campos disponíveis para agrupamento horizontal. */
export type TaskGroupBy = 'phase_id' | 'status' | 'priority' | 'assigned_to';

/** Definição de um grupo (cabeçalho + filtro extra aplicado à RPC). */
interface GroupDef {
  /** Chave única estável (id, valor de enum ou 'none'). */
  key: string;
  /** Rótulo exibido no header. */
  label: string;
  /** Cor sólida (hex) ou class tailwind aplicada ao swatch. */
  colorHex?: string;
  colorClass?: string;
  /** Ícone/avatar opcional (renderizado à esquerda do label). */
  leading?: ReactNode;
  /** Filtros adicionais a mesclar em `baseFilters` para esse grupo. */
  filterPatch: Partial<TaskListFilters>;
}

interface TaskGroupedViewProps {
  /** Filtros já mesclados (filters + lockedFilters + include_completed). */
  baseFilters: TaskListFilters;
  /** Campo de agrupamento escolhido. */
  groupBy: TaskGroupBy;
  /** Ordenação atual (reutiliza a mesma da página). */
  sortBy: string;
  sortDir: string;
  /** Estado padrão dos grupos (preferência persistida). */
  defaultExpanded?: boolean;
  /** Contador que, ao mudar, força todos os grupos a expandir/colapsar. */
  expandAllSignal?: number;
  /** Direção do último comando em massa (true=expandir, false=colapsar). */
  expandAllValue?: boolean;
  /* Compat: handlers legados agora vêm do TaskListTableContext. */
  onViewTask?: (task: TaskListItem) => void;
  onEditTask?: (task: TaskListItem) => void;
  canDoOnPhase?: (phaseId: string, action: any, isResponsible: boolean) => boolean;
  isResponsibleFor?: (task: TaskListItem) => boolean;
}

/** Tamanho da página por grupo (lazy load com "Carregar mais"). */
const GROUP_PAGE_SIZE = 50;

/* =========================================================================
 * Hooks auxiliares para construir a lista de grupos
 * ========================================================================= */

/** Phase + dados da categoria pai (para nível extra "Categoria → Fase"). */
interface PhaseForGrouping {
  id: string;
  name: string;
  color: string;
  sort_order: number;
  category_id: string;
  category_name: string;
  category_color: string;
  category_sort_order: number;
}

/** Busca fases (todas ou filtradas por categoria) para agrupamento por Fase. */
function usePhasesForGrouping(categoryId?: string) {
  const { currentTenant } = useAuth();
  return useQuery<PhaseForGrouping[]>({
    queryKey: ['task-phases-grouping', currentTenant?.id, categoryId || null],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      let query = supabase
        .from('task_phases')
        .select(
          'id, name, color, sort_order, category_id, task_categories!inner(id, name, color, sort_order)'
        )
        .eq('tenant_id', currentTenant.id)
        .order('sort_order', { ascending: true });

      if (categoryId) query = query.eq('category_id', categoryId);

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((row: any) => ({
        id: row.id,
        name: row.name,
        color: row.color,
        sort_order: row.sort_order,
        category_id: row.category_id,
        category_name: row.task_categories?.name ?? '—',
        category_color: row.task_categories?.color ?? '#94a3b8',
        category_sort_order: row.task_categories?.sort_order ?? 0,
      })) as PhaseForGrouping[];
    },
    enabled: !!currentTenant?.id,
    staleTime: 60 * 1000,
  });
}

/* =========================================================================
 * Componente principal
 * ========================================================================= */
export function TaskGroupedView({
  baseFilters,
  groupBy,
  sortBy,
  sortDir,
  defaultExpanded = false,
  expandAllSignal = 0,
  expandAllValue = false,
}: TaskGroupedViewProps) {
  const { data: phases = [] } = usePhasesForGrouping(baseFilters.category_id);
  const { data: members = [] } = useTenantMembers();

  // Aninha Categoria → Fase quando agrupa por Fase sem categoria pré-filtrada
  const phaseHasCategoryNesting =
    groupBy === 'phase_id' && !baseFilters.category_id && phases.length > 0;

  const categoriesWithPhases = useMemo(() => {
    if (!phaseHasCategoryNesting) return [];
    const map = new Map<
      string,
      {
        id: string;
        name: string;
        color: string;
        sort_order: number;
        phases: PhaseForGrouping[];
      }
    >();
    for (const p of phases) {
      const existing = map.get(p.category_id);
      if (existing) {
        existing.phases.push(p);
      } else {
        map.set(p.category_id, {
          id: p.category_id,
          name: p.category_name,
          color: p.category_color,
          sort_order: p.category_sort_order,
          phases: [p],
        });
      }
    }
    return Array.from(map.values()).sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
    );
  }, [phaseHasCategoryNesting, phases]);

  // Constrói a lista de grupos conforme o campo selecionado
  const groups: GroupDef[] = useMemo(() => {
    switch (groupBy) {
      case 'phase_id':
        return phases.map((p) => ({
          key: p.id,
          label: p.name,
          colorHex: p.color,
          filterPatch: { phase_id: p.id, category_id: p.category_id },
        }));
      case 'status': {
        const includeCompleted = baseFilters.include_completed === true;
        const order = includeCompleted
          ? ['pending', 'in_progress', 'paused', 'completed', 'cancelled']
          : ['pending', 'in_progress', 'paused'];
        return order.map((s) => ({
          key: s,
          label: STATUS_LABELS[s] || s,
          filterPatch: { status: s, include_completed: includeCompleted },
        }));
      }
      case 'priority':
        return ['urgent', 'high', 'normal', 'low'].map((p) => ({
          key: p,
          label: PRIORITY_LABELS[p] || p,
          colorClass: PRIORITY_BAR_COLORS[p],
          filterPatch: { priority: p },
        }));
      case 'assigned_to': {
        const list: GroupDef[] = members.map((m: any) => ({
          key: m.id,
          label: m.full_name,
          leading: (
            <SmartAvatar
              src={m.avatar_url}
              name={m.full_name}
              size="xs"
              use3DFallback
            />
          ),
          filterPatch: { assigned_to: m.id },
        }));
        list.push({
          key: 'unassigned',
          label: 'Sem responsável',
          leading: <User className="h-4 w-4 text-muted-foreground" />,
          filterPatch: { assigned_to_is_null: true },
        });
        return list;
      }
      default:
        return [];
    }
  }, [groupBy, phases, members, baseFilters.include_completed]);

  if (phaseHasCategoryNesting) {
    return (
      <div className="flex flex-col">
        {categoriesWithPhases.map((cat) => (
          <TaskCategoryGroupSection
            key={`category:${cat.id}`}
            category={cat}
            baseFilters={baseFilters}
            sortBy={sortBy}
            sortDir={sortDir}
            defaultExpanded={defaultExpanded}
            expandAllSignal={expandAllSignal}
            expandAllValue={expandAllValue}
          />
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-xs text-muted-foreground">
        Carregando opções de agrupamento...
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {groups.map((group) => (
        <TaskGroupSection
          key={`${groupBy}:${group.key}`}
          group={group}
          scope={`${groupBy}:${group.key}`}
          baseFilters={baseFilters}
          sortBy={sortBy}
          sortDir={sortDir}
          defaultExpanded={defaultExpanded}
          expandAllSignal={expandAllSignal}
          expandAllValue={expandAllValue}
        />
      ))}
    </div>
  );
}

/* =========================================================================
 * Seção colapsável de CATEGORIA — envelopa as fases filhas.
 * ========================================================================= */
interface TaskCategoryGroupSectionProps {
  category: {
    id: string;
    name: string;
    color: string;
    phases: PhaseForGrouping[];
  };
  baseFilters: TaskListFilters;
  sortBy: string;
  sortDir: string;
  defaultExpanded?: boolean;
  expandAllSignal?: number;
  expandAllValue?: boolean;
}

function TaskCategoryGroupSection({
  category,
  baseFilters,
  sortBy,
  sortDir,
  defaultExpanded = false,
  expandAllSignal = 0,
  expandAllValue = false,
}: TaskCategoryGroupSectionProps) {
  const [expanded, setExpanded] = useTaskGroupExpandedState(
    `category:${category.id}`,
    defaultExpanded
  );

  // Comando em massa (Expandir/Colapsar todos)
  useEffect(() => {
    if (expandAllSignal > 0) setExpanded(expandAllValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllSignal]);

  const categoryFilters: TaskListFilters = useMemo(
    () => ({ ...baseFilters, category_id: category.id }),
    [baseFilters, category.id]
  );

  const { totalRecords: categoryCount, isLoading: countLoading } =
    useTasksPaginated(categoryFilters, 1, 1, sortBy, sortDir);

  return (
    <div className="border-b border-border/50 last:border-b-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-4 py-3 text-left',
          'bg-muted/20 hover:bg-muted/40 transition-colors',
          expanded && 'bg-muted/40'
        )}
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            expanded && 'rotate-90'
          )}
        />
        <span
          className="w-3.5 h-3.5 rounded-full shrink-0 ring-2 ring-background"
          style={{ backgroundColor: category.color }}
        />
        <span className="text-sm font-semibold truncate">{category.name}</span>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
          {countLoading ? '…' : categoryCount}
        </Badge>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {category.phases.length} fase(s)
        </span>
      </button>

      {expanded && (
        <div className="pl-6 border-l border-border/40 ml-4">
          {category.phases.map((p) => (
            <TaskGroupSection
              key={`phase:${p.id}`}
              group={{
                key: p.id,
                label: p.name,
                colorHex: p.color,
                filterPatch: { phase_id: p.id, category_id: category.id },
              }}
              scope={`phase:${p.id}`}
              baseFilters={baseFilters}
              sortBy={sortBy}
              sortDir={sortDir}
              defaultExpanded={defaultExpanded}
              expandAllSignal={expandAllSignal}
              expandAllValue={expandAllValue}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * Seção colapsável de GRUPO (cabeçalho + conteúdo lazy)
 * ========================================================================= */
interface TaskGroupSectionProps {
  group: GroupDef;
  /** Escopo de persistência local (chave estável para localStorage). */
  scope: string;
  baseFilters: TaskListFilters;
  sortBy: string;
  sortDir: string;
  defaultExpanded?: boolean;
  expandAllSignal?: number;
  expandAllValue?: boolean;
}

function TaskGroupSection({
  group,
  scope,
  baseFilters,
  sortBy,
  sortDir,
  defaultExpanded = false,
  expandAllSignal = 0,
  expandAllValue = false,
}: TaskGroupSectionProps) {
  const [expanded, setExpanded] = useTaskGroupExpandedState(scope, defaultExpanded);

  // Comando em massa (Expandir/Colapsar todos)
  useEffect(() => {
    if (expandAllSignal > 0) setExpanded(expandAllValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandAllSignal]);

  const mergedFilters: TaskListFilters = useMemo(
    () => ({ ...baseFilters, ...group.filterPatch }),
    [baseFilters, group.filterPatch]
  );

  // Contagem leve (perPage=1 — só lê total_records do header)
  const { totalRecords: groupCount, isLoading: countLoading } = useTasksPaginated(
    mergedFilters,
    1,
    1,
    sortBy,
    sortDir
  );
  const showCount = !countLoading;

  return (
    <div className="border-b border-border/50 last:border-b-0">
      {/* Header da seção */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-4 py-2.5 text-left',
          'hover:bg-muted/40 transition-colors',
          expanded && 'bg-muted/30'
        )}
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform',
            expanded && 'rotate-90'
          )}
        />
        {group.leading ? (
          group.leading
        ) : group.colorHex ? (
          <span
            className="w-3 h-3 rounded-full shrink-0"
            style={{ backgroundColor: group.colorHex }}
          />
        ) : group.colorClass ? (
          <span className={cn('w-3 h-3 rounded-full shrink-0', group.colorClass)} />
        ) : null}
        <span className="text-sm font-medium truncate">{group.label}</span>
        <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
          {showCount ? groupCount : '…'}
        </Badge>
      </button>

      {/* Conteúdo lazy — só monta quando expandido */}
      {expanded && (
        <TaskGroupSectionContent
          mergedFilters={mergedFilters}
          sortBy={sortBy}
          sortDir={sortDir}
        />
      )}
    </div>
  );
}

/* =========================================================================
 * Conteúdo lazy de uma seção — Reusa `<TaskListTable />` com scroll horizontal
 * próprio para manter paridade total com a listagem padrão (mesmas colunas,
 * mesmos componentes, mesmo comportamento de subtarefas/sort/bulk edit).
 * ========================================================================= */
interface TaskGroupSectionContentProps {
  mergedFilters: TaskListFilters;
  sortBy: string;
  sortDir: string;
}

function TaskGroupSectionContent({
  mergedFilters,
  sortBy,
  sortDir,
}: TaskGroupSectionContentProps) {
  const [pageSize, setPageSize] = useState(GROUP_PAGE_SIZE);

  const { tasks, totalRecords, isLoading } = useTasksPaginated(
    mergedFilters,
    1,
    pageSize,
    sortBy,
    sortDir
  );

  const hasMore = totalRecords > tasks.length;

  return (
    <div className="bg-background">
      {isLoading ? (
        <div className="space-y-2 px-4 py-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="px-6 py-4 text-xs text-muted-foreground italic">
          Nenhuma tarefa neste grupo.
        </div>
      ) : (
        // overflow-x-auto preserva as larguras nativas da Table padrão e
        // expõe a barra de rolagem horizontal quando necessário (telas
        // estreitas) — exatamente como a listagem normal.
        <div className="overflow-x-auto">
          <TaskListTable tasks={tasks} />
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center py-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPageSize((s) => s + GROUP_PAGE_SIZE)}
            disabled={isLoading}
            className="h-7 text-xs"
          >
            {isLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Carregar mais ({totalRecords - tasks.length} restantes)
          </Button>
        </div>
      )}
    </div>
  );
}
